import type { PolicyReviewRecordDTO } from '@deepguide-ai/types'
import { Download } from 'lucide-react'
import { Button } from '@deepguide-ai/ui/button'
import { Badge } from '@deepguide-ai/ui/badge'
import { Card, CardContent, CardHeader } from '@deepguide-ai/ui/card'
import { MemberAvatar } from '@/components/compliance/MemberAvatar'
import { DetailStatusItem } from '@/components/compliance/detail-primitives'
import { FileExtensionIcon } from '@/utils/file-icons'
import { useTheme } from '@/context/theme-context'

/** Read-only presentation shared by Policy and Document reviews and auditor source records. */
export function PolicyReviewHistory({ records, onViewVersion, onOpenAttachment, onDownloadAttachment, attachmentPending = false }: {
  records: Omit<PolicyReviewRecordDTO, 'policyDocumentVersionId'>[]
  onViewVersion?: (version: number) => void
  onOpenAttachment?: (reviewId: string, fileId: string) => void
  onDownloadAttachment?: (reviewId: string, fileId: string) => void
  attachmentPending?: boolean
}) {
  const { theme } = useTheme()
  if (records.length === 0) return <p className='text-sm text-muted-foreground'>No review records yet.</p>
  return <div className='space-y-3'>
    {records.map((entry) => <Card key={entry.id} className='gap-3 py-4 text-sm shadow-none'>
      <CardHeader className='gap-3 px-4'>
        <div className='flex items-start justify-between gap-3'>
          <div className='flex min-w-0 items-center gap-2'>
            <MemberAvatar person={{ name: entry.reviewerName }} size='sm' className='shrink-0' />
            <div className='min-w-0'>
              <p className='break-words font-medium'>{entry.reviewerName}</p>
              <time dateTime={entry.reviewedAt} className='text-xs text-muted-foreground'>{new Date(entry.reviewedAt).toLocaleString()}</time>
            </div>
          </div>
          {onViewVersion
            ? <Button variant='outline' size='sm' onClick={() => onViewVersion(entry.version)}>View v{entry.version}</Button>
            : <Badge variant='outline'>Version {entry.version}</Badge>}
        </div>
        <Badge variant='secondary' className='max-w-full whitespace-normal'>
          {entry.source === 'approval' ? 'Recorded from approval' : entry.decision === 'no_changes' ? 'Reviewed — no changes required' : 'Changes required'}
        </Badge>
      </CardHeader>
      {(entry.notes || entry.participants.length > 0 || entry.attachments.length > 0) && <CardContent className='space-y-3 px-4'>
        {entry.notes && <DetailStatusItem label='Review notes'>
          <p className='whitespace-pre-wrap break-words'>{entry.notes}</p>
        </DetailStatusItem>}
        {entry.participants.length > 0 && <DetailStatusItem label='Additional participants'>
          <div className='flex flex-wrap gap-2'>
            {entry.participants.map((email) => <Badge key={email} variant='outline' className='max-w-full gap-1.5 whitespace-normal font-normal'>
              <MemberAvatar person={{ email }} size='xs' className='shrink-0' />
              <span className='min-w-0 break-all'>{email}</span>
            </Badge>)}
          </div>
        </DetailStatusItem>}
        {entry.attachments.length > 0 && <DetailStatusItem label='Attachments'>
          <div className='space-y-1'>
            {entry.attachments.map((file) => <div key={file.id} className='flex min-w-0 items-center gap-2'>
              <span aria-hidden='true'><FileExtensionIcon filename={file.name} theme={theme} className='size-4 shrink-0' /></span>
              {onOpenAttachment
                ? <Button variant='link' size='sm' className='min-w-0 justify-start px-0' disabled={attachmentPending} onClick={() => onOpenAttachment(entry.id, file.id)}><span className='truncate'>{file.name}</span></Button>
                : file.url
                  ? <Button variant='link' size='sm' className='min-w-0 justify-start px-0' asChild><a href={file.url} target='_blank' rel='noopener noreferrer'><span className='truncate'>{file.name}</span></a></Button>
                  : <span className='min-w-0 truncate'>{file.name}</span>}
              {onDownloadAttachment && <Button variant='ghost' size='smIcon' className='ml-auto shrink-0' disabled={attachmentPending} title='Download' aria-label={`Download ${file.name}`} onClick={() => onDownloadAttachment(entry.id, file.id)}><Download className='size-4' aria-hidden='true' /></Button>}
            </div>)}
          </div>
        </DetailStatusItem>}
      </CardContent>}
    </Card>)}
  </div>
}
